'use client';
// Радар-диаграмма на чистом SVG — без внешних зависимостей.
// Показывает профиль по шкалам теста как многоугольник.

type RadarPoint = { label: string; value: number };

export default function RadarChart({
  points,
  max = 100,
  size = 280,
}: {
  points: RadarPoint[];
  max?: number;
  size?: number;
}) {
  const n = points.length;

  if (n < 3) return null;

  // Вычисляем максимальную длину лейбла для адаптивного padding
  const maxLabelLen = Math.max(...points.map(p => p.label.length));
  const padX = Math.min(100, Math.max(60, maxLabelLen * 5.5));
  const padY = 50;
  const svgW = size + padX * 2;
  const svgH = size + padY * 2;
  const cx = svgW / 2;
  const cy = svgH / 2;
  const radius = size * 0.36;

  // координаты вершин правильного n-угольника
  const angleFor = (i: number) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const posFor = (i: number, r: number) => ({
    x: cx + r * Math.cos(angleFor(i)),
    y: cy + r * Math.sin(angleFor(i)),
  });

  // концентрические сетки (25%, 50%, 75%, 100%)
  const rings = [0.25, 0.5, 0.75, 1.0];
  const ringPaths = rings.map((frac) => {
    const pts = Array.from({ length: n }, (_, i) => {
      const p = posFor(i, radius * frac);
      return `${p.x},${p.y}`;
    }).join(' ');
    return { frac, path: pts };
  });

  // оси (спицы)
  const axes = points.map((_, i) => posFor(i, radius));

  // полигон данных
  const dataPts = points.map((p, i) => {
    const frac = Math.min(1, Math.max(0, p.value / max));
    return posFor(i, radius * frac);
  });
  const dataPath = dataPts.map((p) => `${p.x},${p.y}`).join(' ');

  // метки — позиционируем чуть дальше радиуса, обрезаем длинные
  const labelOffset = 18;
  const labelPos = points.map((_, i) => posFor(i, radius + labelOffset));

  return (
    <div className="flex justify-center overflow-x-auto">
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} className="max-w-full">
        {/* сетка */}
        {ringPaths.map((r, idx) => (
          <polygon
            key={idx}
            points={r.path}
            fill="none"
            stroke="rgb(212 212 216 / 0.5)"
            strokeWidth="1"
          />
        ))}
        {/* оси */}
        {axes.map((a, i) => (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={a.x}
            y2={a.y}
            stroke="rgb(212 212 216 / 0.4)"
            strokeWidth="1"
          />
        ))}
        {/* полигон данных */}
        <polygon
          points={dataPath}
          fill="rgba(99, 102, 241, 0.18)"
          stroke="rgb(99, 102, 241)"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {/* точки данных */}
        {dataPts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill="rgb(129, 140, 248)" />
        ))}
        {/* метки */}
        {points.map((p, i) => {
          const lp = labelPos[i];
          const dx = lp.x - cx;
          const anchor = Math.abs(dx) < 15 ? 'middle' : dx > 0 ? 'start' : 'end';
          // Обрезаем длинные лейблы до 18 символов
          const short = p.label.length > 18 ? p.label.slice(0, 16) + '…' : p.label;
          return (
            <text
              key={i}
              x={lp.x}
              y={lp.y}
              textAnchor={anchor}
              dominantBaseline="middle"
              className="fill-muted-foreground"
              style={{ fontSize: '10px', fontFamily: 'system-ui' }}
            >
              {short}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
